import { DateHelper, IDateTimeType } from '@connect-soft/date-helper';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useContext } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Checkbox } from '@/fields/Checkbox/Checkbox';
import { ColorPicker } from '@/fields/ColorPicker';
import { DatePicker } from '@/fields/DatePicker';
import { DateRangePicker } from '@/fields/DateRangePicker';
import { MultiChipSelect } from '@/fields/MultiChipSelect';
import { MultiSelect } from '@/fields/MultiSelect';
import { Password } from '@/fields/Password';
import { Select } from '@/fields/Select';
import { Switch } from '@/fields/Switch';
import { TextField } from '@/fields/TextField';
import { TimePicker } from '@/fields/TimePicker';
import { ITimeRangeOrTimeAndLengthValueType, TimeRangeOrTimeAndLength } from '@/fields/TimeRangeOrTimeAndLength';
import { TimeRangePicker } from '@/fields/TimeRangePicker';
import { TransferList } from '@/fields/TransferList';
import { WeekdaysPicker } from '@/fields/WeekdaysPicker';
import { Button } from '@/wrappers/Button';
import { IconButton } from '@/wrappers/IconButton';
import { Tooltip } from '@/wrappers/Tooltip';
import FormSettingsContext, { defaultContextValues, ISettingsContext } from '../FormSettingsContext';
import { ISimpleFieldTypes } from './FieldTypes';
import FieldWrapper from './FieldWrapper';
import { callableValidationOfRequired, getErrorByFieldName, requiredAndDisplay } from './utils';

export type ISimpleFieldRendererProps = {
    hidden?: boolean;
    disabled?: boolean;
    shouldUnregister?: boolean;
    enableDOMRequired?: boolean;
    fullName: string;
    fullWidth: boolean;
    input: ISimpleFieldTypes;
    /** Used only by {@link MultiRowGenerator}. */
    parentName?: string;
    readOnly?: boolean;
    /** Used only by {@link MultiRowGenerator}. */
    rowIndex?: number;
};

const SimpleFieldRenderer = ({
    fullName,
    fullWidth,
    input,
    enableDOMRequired = true,
    shouldUnregister = true,
    hidden = false,
    parentName,
    readOnly,
    rowIndex
}: ISimpleFieldRendererProps) => {
    const { control, watch, getValues } = useFormContext();
    const customSettings = useContext(FormSettingsContext);
    const formSettings: ISettingsContext = {
        ...defaultContextValues,
        ...customSettings
    };

    switch (input.type) {
        case 'button':
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const isDisplayed = input.display ? input.display(getValues()) : true;

                        const button = (
                            <Button
                                data-testid={fullName}
                                {...input.props}
                                disabled={input.props.disabled || readOnly}
                                name={fullName}
                                type={input.props.type === 'cancel' ? 'button' : input.props.type}
                            />
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                type={input.type}
                                hidden={!isDisplayed}
                                fullWidth={fullWidth}
                                width={input.width}
                                sx={input.props.sx}
                            >
                                {input.props.title ? (
                                    <Tooltip color="primary" title={input.props.title}>
                                        {button}
                                    </Tooltip>
                                ) : (
                                    button
                                )}
                            </FieldWrapper>
                        );
                    }}
                />
            );
        case 'custom': {
            const shouldDisplay = input.display ? input.display(watch()) : true;

            return (
                <>
                    {input.props.render({
                        fullName,
                        parentName,
                        rowIndex,
                        disabled: !shouldDisplay
                    })}
                </>
            );
        }
        case 'colorPicker': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <ColorPicker
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    data-testid={`${fullName}-colorPicker`}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'checkBox': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <FormControlLabel
                                    label={input.props.label}
                                    control={
                                        <Checkbox
                                            {...input.props}
                                            {...field}
                                            {...inputDynamicConfig}
                                            enableDOMAttributeRequired={enableDOMRequired}
                                            readOnly={input.props.readOnly || readOnly}
                                            onChange={(_, isChecked) => field.onChange(isChecked)}
                                        />
                                    }
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'datePicker': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <DatePicker
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    // inputFormat={input.props.inputFormat}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'dateRangePicker': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        validate: (currentValue: { start: IDateTimeType | null; end: IDateTimeType | null }) => {
                            const inputDynamicConfig = requiredAndDisplay(
                                getValues,
                                input.props.required,
                                input.display,
                                hidden
                            );

                            if (inputDynamicConfig.required && !inputDynamicConfig.hidden) {
                                if (currentValue.start === null || currentValue.end === null) {
                                    return formSettings.messages.required;
                                } else if (DateHelper.from(currentValue.start).isAfter(currentValue.end)) {
                                    return formSettings.messages.invalidValuesFromTo;
                                }
                            } else if (currentValue.start !== null || currentValue.end !== null) {
                                return currentValue.start !== null &&
                                    currentValue.end !== null &&
                                    DateHelper.from(currentValue.start).isAfter(currentValue.end)
                                    ? formSettings.messages.invalidValuesFromTo
                                    : undefined;
                            }

                            return undefined;
                        }
                    }}
                    render={({ field: { ref: _, ...field }, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <DateRangePicker
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    // inputFormat={input.props.inputFormat}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'html': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(getValues, false, input.display, hidden);

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                {input.props.render()}
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'iconButton':
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const isDisplayed = input.display ? input.display(getValues()) : true;
                        const isDisabled =
                            typeof input.props.disabled === 'function'
                                ? input.props.disabled(getValues())
                                : Boolean(input.props.disabled ?? false);

                        return (
                            <FieldWrapper
                                error={!!error}
                                type="iconButton"
                                fullWidth={fullWidth}
                                hidden={!isDisplayed}
                                sx={input.sx}
                                width={input.width}
                            >
                                <IconButton
                                    {...input.props}
                                    disabled={isDisabled || readOnly}
                                    name={fullName}
                                    type={input.props.type === 'cancel' ? 'button' : input.props.type}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        case 'multiChipSelect': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <MultiChipSelect
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'multiSelect': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <MultiSelect
                                    data-testid={`multiselect-${field.name}`}
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'newLine':
            return <></>;
        case 'password': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <Password
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'select': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );
                        const value =
                            typeof input.props.value === 'function'
                                ? input.props.value(inputDynamicConfig.values)
                                : field.value;

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <Select
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                    value={value}
                                    onChange={(newValue) => {
                                        if (input.props.onChange) {
                                            input.props.onChange(newValue);
                                        }

                                        field.onChange(newValue);
                                    }}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'switch': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <Switch
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    readOnly={input.props.readOnly || readOnly}
                                    onChange={(newValue) => {
                                        if (input.props.onChange) {
                                            input.props.onChange(newValue.target.checked);
                                        }

                                        field.onChange(newValue);
                                    }}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'timePicker': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <TimePicker
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    // inputFormat={input.props.inputFormat}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'timeRangePicker': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        validate: (currentValue: { start: IDateTimeType | null; end: IDateTimeType | null }) => {
                            const inputDynamicConfig = requiredAndDisplay(
                                getValues,
                                input.props.required,
                                input.display,
                                hidden
                            );

                            if (inputDynamicConfig.required && !inputDynamicConfig.hidden) {
                                if (currentValue.start === null || currentValue.end === null) {
                                    return formSettings.messages.required;
                                } else if (DateHelper.from(currentValue.start).isAfter(currentValue.end)) {
                                    return formSettings.messages.invalidValuesFromTo;
                                }
                            } else if (currentValue.start !== null || currentValue.end !== null) {
                                return currentValue.start !== null &&
                                    currentValue.end !== null &&
                                    DateHelper.from(currentValue.start).isAfter(currentValue.end)
                                    ? formSettings.messages.invalidValuesFromTo
                                    : undefined;
                            }

                            return undefined;
                        }
                    }}
                    render={({ field: { ref: _, ...field }, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <TimeRangePicker
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                    // inputFormat={input.props.inputFormat}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'timeRangeOrTimeAndLength': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        required: formSettings.messages.required,
                        validate: (currentValue: {
                            start: IDateTimeType | null;
                            duration: number | null;
                            isRange: boolean;
                        }) => {
                            const inputDynamicConfig = requiredAndDisplay(
                                getValues,
                                input.props.required,
                                input.display,
                                hidden
                            );

                            if (inputDynamicConfig.required && !inputDynamicConfig.hidden) {
                                if (currentValue.start === null || currentValue.duration === null) {
                                    return formSettings.messages.required;
                                } else if (currentValue.duration <= 0) {
                                    return formSettings.messages.invalidRangeOfValues;
                                }
                            } else if (currentValue.start !== null || currentValue.duration !== null) {
                                return (typeof currentValue.duration === 'number' && currentValue.duration <= 0) ||
                                    currentValue.start !== null
                                    ? formSettings.messages.invalidRangeOfValues
                                    : undefined;
                            }

                            return undefined;
                        }
                    }}
                    render={({ field: { ref: _, ...field }, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <TimeRangeOrTimeAndLength
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    error={!!error}
                                    helperText={error?.message ?? input.props.helperText}
                                    labelRange={formSettings.labels.range}
                                    minStart={
                                        typeof input.props.minStart === 'function'
                                            ? input.props.minStart(getValues())
                                            : input.props.minStart
                                    }
                                    maxDuration={
                                        typeof input.props.maxDuration === 'function'
                                            ? input.props.maxDuration(getValues())
                                            : input.props.maxDuration
                                    }
                                    name={fullName}
                                    readOnly={input.props.readOnly || readOnly}
                                    onChange={(value) => {
                                        const currentValue = field.value as ITimeRangeOrTimeAndLengthValueType;

                                        field.onChange({
                                            ...value,
                                            changedValue:
                                                currentValue.duration !== value.duration ||
                                                !DateHelper.from(currentValue?.start).isEqual(value?.start ?? null)
                                        });
                                    }}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'textArea': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );
                        const error = getErrorByFieldName(errors, field.name);

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <TextField
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    value={field.value ?? input.props.value ?? ''}
                                    multiline
                                    data-testid={`text_area_field_${fullName}`}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    minRows={input.props.rows || input.props.minRows || 3}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'textField': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const disabled =
                            typeof input.props.disabled === 'function'
                                ? input.props.disabled(getValues())
                                : input.props.disabled;
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <TextField
                                    data-testid={`text_field_${fullName}`}
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    disabled={disabled}
                                    enableDOMAttributeRequired={enableDOMRequired}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                    onChange={(event) => {
                                        if (input.props.onChange) {
                                            input.props.onChange(event.target.value);
                                        }

                                        field.onChange(event);
                                    }}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'transferList': {
            return (
                <Controller
                    name={fullName}
                    control={control}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        ...(typeof input.props.required !== 'function'
                            ? {
                                  required: input.props.required ? formSettings.messages.required : undefined
                              }
                            : {
                                  validate: (currentValue) =>
                                      callableValidationOfRequired(
                                          currentValue,
                                          getValues,
                                          formSettings.messages.required,
                                          input.props.required,
                                          input.display,
                                          hidden
                                      )
                              })
                    }}
                    render={({ field: { ref: _, ...field }, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <TransferList
                                    {...input.props}
                                    {...field}
                                    {...inputDynamicConfig}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    readOnly={input.props.readOnly || readOnly}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        case 'weekdays': {
            return (
                <Controller
                    control={control}
                    name={fullName}
                    shouldUnregister={shouldUnregister}
                    rules={{
                        ...input.validation,
                        validate: (currentValue: number) =>
                            input.props.required && currentValue === 0 ? formSettings.messages.required : undefined
                    }}
                    render={({ field, formState: { errors } }) => {
                        const error = getErrorByFieldName(errors, field.name);
                        const inputDynamicConfig = requiredAndDisplay(
                            getValues,
                            input.props.required,
                            input.display,
                            hidden
                        );

                        return (
                            <FieldWrapper
                                error={!!error}
                                hidden={inputDynamicConfig.hidden}
                                fullWidth={fullWidth}
                                sx={input.sx}
                                type={input.type}
                                width={input.width}
                            >
                                <WeekdaysPicker
                                    {...input.props}
                                    {...inputDynamicConfig}
                                    error={!!error}
                                    helperText={error ? error.message : input.props.helperText}
                                    name={field.name}
                                    readOnly={input.props.readOnly || readOnly}
                                    value={field.value}
                                    onChange={field.onChange}
                                />
                            </FieldWrapper>
                        );
                    }}
                />
            );
        }
        default:
            // eslint-disable-next-line no-console
            console.error(input);
            new Error('Not supported');
    }

    return <></>;
};

export default SimpleFieldRenderer;
